import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { MenuItem } from '../common/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import VipIcon from '../Icons/VipIcons';
import { NAVIGATION } from '../../constants/staticTexts';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { messageService } from '../../services/messageService';
import { toast } from '../../utils/notify';

import { CIRCLES_PAGE_URL, DASHBOARD_PAGE_URL, PURCHASES_PAGE_URL, SCHEDULED_PAGE_URL, SETTINGS_PAGE_URL, HELP_DESK_URL, SUBSCRIPTIONS_PAGE_URL, MEDIA_LIBRARY_PAGE_URL, CATEGORIES_PAGE_URL, MESSAGE_PAGE_URL } from '../../constants/systemConstants';

const MoreMenuContainer = styled.div`
  position: relative;
  z-index: 2147483646 !important; /* Just below maximum to ensure proper hierarchy */
  isolation: isolate;
  
  /* Hide MoreMenu on desktop since all items are shown in sidebar */
  @media(min-width: 769px) {
    display: none;
  }
`;

const MoreMenuOverlay = styled.div`
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 2147483647 !important; /* Maximum possible z-index value */
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  /* Force new stacking context */
  transform: translateZ(0);
  will-change: transform;
  isolation: isolate;
`;

const MoreMenuPanel = styled.div`
  position: fixed !important;
  bottom: 85px;
  left: 50%;
  transform: translateX(-50%) ${props => props.$isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)'};
  background: ${props => props.theme.colors.bg2 || 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(35, 35, 35, 0.95) 100%)'};
  border: 1px solid ${props => props.theme.colors.border || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 20px;
  padding: 8px;
  width: 320px;
  max-width: 90vw;
  box-shadow: ${props => props.theme.colors.shadow 
    ? `0 20px 40px ${props.theme.colors.shadow}, 0 0 0 1px ${props.theme.colors.border}, inset 0 1px 0 rgba(255, 255, 255, 0.1)`
    : '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
  };
  z-index: 2147483647 !important; /* Maximum possible z-index value */
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  /* Force new stacking context and ensure it's always on top */
  will-change: transform;
  isolation: isolate;

  @media (max-width: 768px) {
    bottom: 75px;
    width: 280px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    background: ${props => props.theme.colors.borderGradient || 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'};
    border-radius: 20px;
    z-index: -1; /* Keep this relative to its parent */
  }
`;

const MenuSection = styled.div`
  padding: 0 8px;
`;

const MoreMenuItem = styled(MenuItem)`
  gap: 16px;
  border-radius: 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  
  &:hover:not(:disabled) {
    background: ${props => props.$isActive
      ? props.theme.colors.primaryHover || 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(79, 70, 229, 0.2) 100%)'
      : props.theme.colors.hover || 'rgba(255, 255, 255, 0.08)'
    };
    color: ${props => props.theme.colors.text || '#fff'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  ${props => props.$isActive && `
    background: ${props.theme.colors.primaryLight || 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(79, 70, 229, 0.15) 100%)'};
    border: 1px solid ${props.theme.colors.primary || 'rgba(147, 51, 234, 0.3)'};
    color: ${props.theme.colors.text || '#fff'};
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${props.theme.colors.primaryGradient || 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.1) 100%)'};
      z-index: -1;
    }
  `}
`;

const MoreMenuItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: inherit;
`;

const MoreMenuItemText = styled.span`
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: inherit;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.borderGradient || 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)'};
`;

const LogoutSection = styled.div`
  padding: 8px 8px 12px;
`;

const LogoutButton = styled(MoreMenuItem)`
  color: ${props => props.theme.colors.danger || 'rgba(239, 68, 68, 0.9)'};
  
  &:hover {
    background: ${props => props.theme.colors.errorLight || 'rgba(239, 68, 68, 0.1)'};
    color: ${props => props.theme.colors.errorHover || '#ef4444'};
  }
`;

const MoreMenu = ({ isOpen, onClose, onLogout }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef();

  // Helper to check accessSpec permissions
  const hasAccess = (feature) => {
    // If no accessSpec, use isCreator as default
    if (!currentUser?.accessSpec) {
      return currentUser?.creator ?? false;
    }
    return currentUser.accessSpec[feature] ?? false;
  };

  // Open (or create) the DM conversation with the Support User.
  const handleContactSupport = async () => {
    try {
      const conversation = await messageService.getSupportConversation();
      if (conversation?.id) {
        navigate(`${MESSAGE_PAGE_URL}/${conversation.id}`);
      } else {
        toast.error('Support is currently unavailable');
      }
    } catch (err) {
      if (err?.code === 'SUPPORT_UNAVAILABLE') {
        toast.error('Support is currently unavailable');
      } else {
        toast.error('Could not open support chat. Please try again.');
      }
    }
  };

  // Build menu items dynamically based on access permissions
  const menuItems = [
    ...(currentUser && !(currentUser.creator || currentUser.isCreator) ? [{
      label: 'Become a Creator',
      icon: <VipIcon name="Plus" size={20} />,
      action: () => navigate(SETTINGS_PAGE_URL, { state: { activeTab: 'creator' } }),
    }] : []),
    {
      label: NAVIGATION.CIRCLES,
      icon: <VipIcon name="Circles" size={20} />,
      to: CIRCLES_PAGE_URL,
    },
    ...(currentUser?.creator ? [{
      label: "Planned Releases",
      icon: <VipIcon name="Clock" size={20} />,
      to: SCHEDULED_PAGE_URL,
    }] : []),
    ...(currentUser?.creator ? [{
      label: "Post Categories",
      icon: <VipIcon name="PostCategories" size={20} />,
      to: CATEGORIES_PAGE_URL,
    }] : []),
    ...(currentUser?.creator ? [{
      label: "Media Library",
      icon: <VipIcon name="Gallery" size={20} />,
      to: MEDIA_LIBRARY_PAGE_URL,
    }] : []),
    // Only show Dashboard if user has access
    ...(hasAccess('canAccessDashboard') ? [{
      label: NAVIGATION.DASHBOARD,
      icon: <VipIcon name="Dashboard" size={20} />,
      to: DASHBOARD_PAGE_URL,
    }] : []),
    {
      label: NAVIGATION.MY_PURCHASES,
      icon: <VipIcon name="ImageIcon" size={20} />,
      to: PURCHASES_PAGE_URL,
    },
    {
      label: NAVIGATION.MY_SUBSCRIPTIONS,
      icon: <VipIcon name="StarIcon" size={20} />,
      to: SUBSCRIPTIONS_PAGE_URL,
    },
    {
      label: NAVIGATION.SETTINGS,
      icon: <VipIcon name="Settings" size={20} />,
      to: SETTINGS_PAGE_URL,
    },
    {
      label: NAVIGATION.MY_PROFILE,
      icon: <VipIcon name="Profile" size={20} />,
      to: currentUser?.username ? `/${currentUser.username}` : SETTINGS_PAGE_URL,
    },
    {
      label: 'Message Support',
      icon: <VipIcon name="Messages" size={20} />,
      action: handleContactSupport,
    },
    {
      label: 'Help Center',
      icon: <VipIcon name="HelpSupport" size={20} />,
      to: HELP_DESK_URL,
    }
  ];

  const handleItemClick = (item) => {
    try {
      if (item.action) {
        item.action();
      } else if (item.to) {
        navigate(item.to);
      }
      onClose();
    } catch (error) {
      console.error('Error in handleItemClick:', error);
    }
  };

  const handleLogout = () => {
    try {
      onLogout();
      onClose();
    } catch (error) {
      console.error('Error in handleLogout:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  useBodyScrollLock(isOpen);

  return isOpen ? createPortal(
    <MoreMenuContainer>
      <MoreMenuOverlay $isOpen={isOpen} onClick={onClose} />
      <MoreMenuPanel $isOpen={isOpen} ref={menuRef}>
        
        <MenuSection>
          {menuItems.map((item, index) => (
            <MoreMenuItem
              key={item.label}
              $isActive={location.pathname === item.to}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleItemClick(item);
              }}
            >
              <MoreMenuItemIcon>
                {item.icon}
              </MoreMenuItemIcon>
              <MoreMenuItemText>
                {item.label}
              </MoreMenuItemText>
            </MoreMenuItem>
          ))}
        </MenuSection>
        
        <MenuDivider />
        
        <LogoutSection>
          <LogoutButton onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLogout();
          }}>
            <MoreMenuItemIcon>
              <VipIcon name="Logout" size={20} />
            </MoreMenuItemIcon>
            <MoreMenuItemText>
              {NAVIGATION.LOGOUT}
            </MoreMenuItemText>
          </LogoutButton>
        </LogoutSection>
      </MoreMenuPanel>
    </MoreMenuContainer>,
    document.body
  ) : null;
};

export default MoreMenu;
